import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { getLevelName, getReadingStage } from '../../config/levelConfig.js';
import { HomeTab } from './tabs/HomeTab.jsx';
import { ProgressTab } from './tabs/ProgressTab.jsx';
import { WordsTab } from './tabs/WordsTab.jsx';
import { SettingsTab } from './tabs/SettingsTab.jsx';
import { fadeIn, tapScale } from '../../lib/motionPresets.js';

const TABS = [
  { id: 'home', label: 'Home', icon: '🏠' },
  { id: 'progress', label: 'Progress', icon: '📈' },
  { id: 'words', label: 'Words', icon: '📚' },
  { id: 'settings', label: 'Settings', icon: '⚙️' }
];

const STAGE_RING_COLORS = {
  'Beginning Reader': 'var(--color-finley-accent)',
  'Early Reader': 'var(--color-finley-warning)',
  'Growing Reader': 'var(--color-finley-primary)',
  'Strong Reader': 'var(--color-finley-secondary)',
  'Fluent Reader': 'var(--color-finley-success)'
};

export function ParentDashboard({ profiles = [], activeId, initialTab = 'home', onClose, onSelectChild, onStartSession, onExtraPractice, onOpenStories, onAddChild, renderProgress, renderSettings, settingsTabProps = {} }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const contentRef = useRef(null);
  const activeProfile = useMemo(() => profiles.find((p) => p.id === activeId) || profiles[0] || null, [profiles, activeId]);
  const stage = getReadingStage(activeProfile?.tracker?.currentLevel || 1);
  const levelName = getLevelName(activeProfile?.tracker?.currentLevel || 1);
  const isSingleChild = profiles.length <= 1;

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeTab]);

  if (!profiles.length) {
    return (
      <motion.div {...fadeIn} className="relative flex h-full flex-col bg-finley-bg">
        <div className="border-b-2 border-finley-text-muted/15 bg-finley-bg-card px-4 pb-3 pt-4 shadow-finley-sm">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-finley-display text-xl text-finley-text">Parent Dashboard</div>
              <div className="text-sm text-finley-text-light">Add your first child profile to begin.</div>
            </div>
            <motion.button {...tapScale} onClick={onClose} className="min-h-[44px] rounded-finley-full bg-finley-bg px-4 py-2 text-sm text-finley-text-light shadow-finley-sm">✕ Close</motion.button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center px-5">
          <div className="w-full max-w-md rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-6 text-center shadow-finley-sm">
            <div className="mb-2 text-4xl">👶</div>
            <div className="mb-2 font-finley-display text-2xl text-finley-text">No child profiles yet</div>
            <p className="mb-4 text-sm text-finley-text-light">Create a child profile to unlock sessions, progress, and insights.</p>
            <motion.button {...tapScale} onClick={onAddChild} className="min-h-[44px] rounded-finley-lg bg-finley-primary px-5 py-3 text-sm font-semibold text-white shadow-finley-sm">+ Add Child</motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="relative flex h-full flex-col bg-finley-bg">
      <div className="border-b-2 border-finley-text-muted/15 bg-finley-bg-card px-4 pb-3 pt-4 shadow-finley-sm">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-finley-display text-xl text-finley-text">Parent Dashboard</div>
            <div className="text-sm text-finley-text-light">{levelName}</div>
          </div>
          <motion.button {...tapScale} onClick={onClose} className="min-h-[44px] rounded-finley-full bg-finley-bg px-4 py-2 text-sm text-finley-text-light shadow-finley-sm">✕ Close</motion.button>
        </div>

        {isSingleChild ? (
          <div className="mb-2 flex items-center gap-3 rounded-finley-lg border-2 border-finley-text-muted/15 bg-finley-bg px-3 py-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-finley-full border-2 border-finley-primary/40 bg-finley-primary/10 text-base font-finley-display text-finley-primary">
              {activeProfile?.avatar || activeProfile?.name?.[0]?.toUpperCase() || 'C'}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-finley-text">{activeProfile?.name || 'Child'}</div>
              <div className="truncate text-xs text-finley-text-light">{levelName}</div>
            </div>
          </div>
        ) : (
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-start gap-2 overflow-x-auto pb-1">
              {profiles.map((profile) => {
                const isActive = profile.id === activeProfile?.id;
                const childStage = getReadingStage(profile?.tracker?.currentLevel || 1);
                const ringColor = STAGE_RING_COLORS[childStage?.stageName] || 'var(--color-finley-primary)';
                return (
                  <motion.button
                    key={profile.id}
                    {...tapScale}
                    onClick={() => onSelectChild && onSelectChild(profile.id)}
                    className={`flex flex-col items-center ${isActive ? 'min-w-[64px]' : 'min-w-[44px]'} pt-0.5`}
                    animate={{ opacity: isActive ? 1 : 0.72 }}
                    transition={{ duration: 0.24 }}
                  >
                    <motion.div
                      className="flex items-center justify-center rounded-finley-full bg-finley-bg font-finley-display text-finley-text shadow-finley-sm"
                      animate={{
                        width: isActive ? 48 : 36,
                        height: isActive ? 48 : 36,
                        borderWidth: isActive ? 3 : 2,
                        borderColor: isActive ? ringColor : 'rgba(148,163,184,0.35)'
                      }}
                      transition={{ duration: 0.24 }}
                      style={{ borderStyle: 'solid' }}
                    >
                      {profile?.avatar || profile?.name?.[0]?.toUpperCase() || 'C'}
                    </motion.div>
                    <div className={`mt-1 max-w-[64px] truncate text-center text-[11px] font-semibold ${isActive ? 'text-finley-text' : 'text-transparent'}`}>
                      {profile?.name || 'Child'}
                    </div>
                  </motion.button>
                );
              })}
            </div>
            <motion.button
              {...tapScale}
              onClick={onAddChild}
              className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-finley-full border-2 border-finley-text-muted/25 bg-finley-bg text-lg font-semibold text-finley-text-light shadow-finley-sm"
              aria-label="Add child"
            >
              +
            </motion.button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="rounded-finley-full bg-finley-primary/10 px-3 py-1 text-xs font-semibold text-finley-primary">{levelName}</span>
          {stage?.stageName && <span className="rounded-finley-full bg-finley-secondary/10 px-3 py-1 text-xs font-semibold text-finley-secondary">{stage.stageName}</span>}
        </div>
      </div>

      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto pb-24"
      >
        <AnimatePresence mode="wait">
          <motion.div key={`${activeTab}:${activeProfile?.id || 'none'}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.24 }}>
            {activeTab === 'home' && (
              <HomeTab
                profile={activeProfile}
                profiles={profiles}
                activeId={activeProfile?.id}
                onSelectChild={onSelectChild}
                onStartSession={onStartSession || onClose}
                onExtraPractice={onExtraPractice || onClose}
                onOpenStories={onOpenStories || onClose}
                onOpenSettings={() => setActiveTab('settings')}
              />
            )}
            {activeTab === 'progress' && <ProgressTab profile={activeProfile} onStartSession={onStartSession || onClose}>{renderProgress ? renderProgress(activeProfile) : null}</ProgressTab>}
            {activeTab === 'words' && <WordsTab profile={activeProfile} onStartSession={onStartSession || onClose} />}
            {activeTab === 'settings' && (
              <SettingsTab
                profile={activeProfile}
                profiles={profiles}
                activeId={activeProfile?.id}
                onSelectChild={onSelectChild}
                {...settingsTabProps}
              >
                {renderSettings ? renderSettings(activeProfile) : null}
              </SettingsTab>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 left-0 right-0 border-t-2 border-finley-text-muted/15 bg-finley-bg-card px-2 pb-[calc(env(safe-area-inset-bottom)+8px)] pt-2 shadow-finley-lg">
        <div className="grid grid-cols-4 gap-1">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <motion.button
                key={tab.id}
                {...tapScale}
                onClick={() => setActiveTab(tab.id)}
                className={`min-h-[56px] rounded-finley-md px-2 py-2 text-center ${
                  isActive ? 'bg-finley-primary/10 text-finley-primary' : 'text-finley-text-light'
                }`}
              >
                <div className="text-lg">{tab.icon}</div>
                <div className="text-xs font-semibold">{tab.label}</div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
